import { addDays, endOfMonth, endOfWeek, format, isSameMonth, isToday, startOfMonth, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { EVENT_TYPE_CLASSES, UnifiedEvent } from '@/hooks/useUnifiedCalendar';
import { CalendarEventTooltip } from '@/components/calendar/CalendarEventTooltip';

interface Props {
  referenceDate: Date;
  events: UnifiedEvent[];
  onSelect: (event: UnifiedEvent) => void;
  nameById?: Map<string, string>;
}

const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

export function UnifiedMonthGrid({ referenceDate, events, onSelect, nameById = new Map() }: Props) {
  const gridStart = startOfWeek(startOfMonth(referenceDate), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(referenceDate), { weekStartsOn: 1 });

  const days: Date[] = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);

  const byDate = new Map<string, UnifiedEvent[]>();
  events.forEach((e) => {
    const list = byDate.get(e.date) ?? [];
    list.push(e);
    byDate.set(e.date, list);
  });

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="grid grid-cols-7 bg-muted/50">
        {WEEKDAYS.map((d) => (
          <div key={d} className="px-2 py-2 text-xs font-medium text-muted-foreground text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayEvents = byDate.get(key) ?? [];
          const milestones = dayEvents.filter((e) => e.kind === 'milestone');
          const sessions = dayEvents.filter((e) => e.kind === 'session');
          return (
            <div
              key={key}
              className={cn(
                'min-h-[110px] border-t border-l border-border p-1.5 space-y-1',
                !isSameMonth(day, referenceDate) && 'bg-muted/30',
              )}
            >
              <div
                className={cn(
                  'text-xs font-medium',
                  isToday(day)
                    ? 'inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {format(day, 'd', { locale: es })}
              </div>
              {milestones.map((e) => (
                <CalendarEventTooltip key={e.id} event={e} nameById={nameById}>
                  <button
                    onClick={() => onSelect(e)}
                    className={cn(
                      'w-full text-left truncate rounded border-l-4 px-1 py-0.5 text-[11px] font-medium',
                      EVENT_TYPE_CLASSES[e.type],
                    )}
                  >
                    {e.title}
                  </button>
                </CalendarEventTooltip>
              ))}
              {sessions.map((e) => (
                <CalendarEventTooltip key={e.id} event={e} nameById={nameById}>
                  <button
                    onClick={() => onSelect(e)}
                    className={cn(
                      'w-full text-left truncate rounded border px-1 py-0.5 text-[11px]',
                      EVENT_TYPE_CLASSES[e.type],
                    )}
                  >
                    {e.startTime ? `${e.startTime} · ` : ''}
                    {e.title}
                  </button>
                </CalendarEventTooltip>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
